/**
 * Which SRP query params a crawler may follow. robots.txt is generated by rpjava
 * (`handler/RobotsTool.java`), so this changes nothing at runtime — it makes the decision
 * reviewable in the repo that owns the params, and makes an unclassified param a `tsc`
 * failure. Bounded params stay crawlable because a filtered SRP canonicalises to a real
 * slug, and a disallowed URL is never crawled, so blocking it hides that canonical. See
 * HP-9138.
 */
import { FILTER_URL_KEYS } from '@/src/lib/filters/filterTypes';

// Separate tuples: carryover forwards the viewport only when all of lat/lon/z are present,
// and `border` must not count towards that.
export const SRP_VIEWPORT_URL_KEYS = ['lat', 'lon', 'z'] as const;
export const SRP_MAP_STATE_URL_KEYS = ['border'] as const;
export const SRP_MAP_URL_KEYS = [...SRP_VIEWPORT_URL_KEYS, ...SRP_MAP_STATE_URL_KEYS] as const;

export const SRP_PAGE_URL_KEY = 'page';

export type SrpUrlParam =
  | (typeof FILTER_URL_KEYS)[number]
  | (typeof SRP_MAP_URL_KEYS)[number]
  | typeof SRP_PAGE_URL_KEY;

export type ParamCrawlClass = 'bounded' | 'unbounded';

// `price`, `sqft` and `avail` take an arbitrary min-max, so the space is large rather than
// small. Bounded here because it is finite, no link generates combinations, and each
// canonicalises onto a slug. First to move if crawl logs show them being enumerated.
export const PARAM_CRAWL_CLASS: Record<SrpUrlParam, ParamCrawlClass> = {
  price: 'bounded',
  beds: 'bounded',
  baths: 'bounded',
  pets: 'bounded',
  laundry: 'bounded',
  amenities: 'bounded',
  furnished: 'bounded',
  propertyTypes: 'bounded',
  sqft: 'bounded',
  createdWithin: 'bounded',
  rentalTypes: 'bounded',
  restrictions: 'bounded',
  avail: 'bounded',
  additionalOpts: 'bounded',
  orderBy: 'bounded',
  page: 'bounded',
  commuteMode: 'bounded',
  commuteTime: 'bounded',
  commuteTimeMode: 'bounded',
  keywords: 'unbounded',
  commuteName: 'unbounded',
  commuteLats: 'unbounded',
  commuteLons: 'unbounded',
  lat: 'unbounded',
  lon: 'unbounded',
  z: 'unbounded',
  border: 'unbounded',
};

function paramsOfClass(target: ParamCrawlClass): ReadonlyArray<SrpUrlParam> {
  return (Object.keys(PARAM_CRAWL_CLASS) as Array<SrpUrlParam>)
    .filter((key) => PARAM_CRAWL_CLASS[key] === target)
    .sort();
}

export const UNBOUNDED_URL_PARAMS: ReadonlyArray<SrpUrlParam> = paramsOfClass('unbounded');
export const BOUNDED_URL_PARAMS: ReadonlyArray<SrpUrlParam> = paramsOfClass('bounded');

export function isCrawlable(param: string): boolean {
  return PARAM_CRAWL_CLASS[param as SrpUrlParam] !== 'unbounded';
}

// Anchored to a param boundary: a bare `/*z=` matches that substring anywhere, including
// inside a value, which is how the rule this replaces (`/*page*&`) blocked `/pagedale-mo`.
export function expectedRobotsDisallowRules(): ReadonlyArray<string> {
  return UNBOUNDED_URL_PARAMS.flatMap((param) => [`/*?${param}=`, `/*&${param}=`]);
}
